import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { transformResponseHandler } from 'utils/transformResponseHandler';

export const inventoryApi = createApi({
    reducerPath: 'inventoryApi',
    baseQuery: fetchBaseQuery({
        baseUrl: `${process.env.REACT_APP_API_ROUTE}`,
        prepareHeaders: (headers, { getState }) => {
            const token = getState()?.posData?.token;
            if (token) {
                headers.set('Authorization', token);
            }
            return headers;
        },
    }),
    tagTypes: ['InventoryCategories', 'InventoryItems', 'Recipes', 'Wastages', 'StockAdjustments', 'InventoryDashboard'],
    endpoints: (builder) => ({
        // Categories
        getInventoryCategories: builder.query({
            query: (search) => ({
                url: `/pos/inventory-categories?perPage=all${search ? `&search=${search}` : ''}`,
                method: 'GET',
            }),
            providesTags: ['InventoryCategories'],
            transformErrorResponse: (error) => transformResponseHandler(error)
        }),
        addInventoryCategory: builder.mutation({
            query: (body) => ({
                url: '/pos/inventory-categories',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['InventoryCategories'],
            transformResponse: (resp) => transformResponseHandler(resp),
            transformErrorResponse: (error) => transformResponseHandler(error)
        }),
        updateInventoryCategory: builder.mutation({
            query: ({ id, ...body }) => ({
                url: `/pos/inventory-categories/${id}`,
                method: 'PUT',
                body,
            }),
            invalidatesTags: ['InventoryCategories'],
            transformResponse: (resp) => transformResponseHandler(resp),
            transformErrorResponse: (error) => transformResponseHandler(error)
        }),
        deleteInventoryCategory: builder.mutation({
            query: (id) => ({
                url: `/pos/inventory-categories/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['InventoryCategories'],
            transformResponse: (resp) => transformResponseHandler(resp),
            transformErrorResponse: (error) => transformResponseHandler(error)
        }),
        changeInventoryCategoryStatus: builder.mutation({
            query: ({ id, body }) => ({
                url: `/pos/inventory-categories/change-status/${id}`,
                method: 'PUT',
                body: { status: body },
            }),
            invalidatesTags: ['InventoryCategories'],
            transformResponse: (resp) => transformResponseHandler(resp),
            transformErrorResponse: (error) => transformResponseHandler(error)
        }),

        // Items
        getInventoryItems: builder.query({
            query: ({ search, category_id, status, page, perPage } = {}) => {
                const params = {};
                if (search) params.search = search;
                if (category_id) params.category_id = category_id;
                if (status) params.status = status;
                if (page) params.page = page;
                if (perPage) params.perPage = perPage;
                return {
                    url: '/pos/inventory-items',
                    method: 'GET',
                    params,
                };
            },
            providesTags: ['InventoryItems'],
            transformErrorResponse: (error) => transformResponseHandler(error)
        }),
        addInventoryItem: builder.mutation({
            query: (body) => ({
                url: '/pos/inventory-items',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['InventoryItems', 'InventoryCategories'],
            transformResponse: (resp) => transformResponseHandler(resp),
            transformErrorResponse: (error) => transformResponseHandler(error)
        }),
        updateInventoryItem: builder.mutation({
            query: ({ id, ...body }) => ({
                url: `/pos/inventory-items/${id}`,
                method: 'PUT',
                body,
            }),
            invalidatesTags: ['InventoryItems', 'InventoryCategories'],
            transformResponse: (resp) => transformResponseHandler(resp),
            transformErrorResponse: (error) => transformResponseHandler(error)
        }),
        deleteInventoryItem: builder.mutation({
            query: (id) => ({
                url: `/pos/inventory-items/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['InventoryItems', 'InventoryCategories'],
            transformResponse: (resp) => transformResponseHandler(resp),
            transformErrorResponse: (error) => transformResponseHandler(error)
        }),
        changeInventoryItemStatus: builder.mutation({
            query: ({ id, body }) => ({
                url: `/pos/inventory-items/change-status/${id}`,
                method: 'PUT',
                body: { status: body },
            }),
            invalidatesTags: ['InventoryItems'],
            transformResponse: (resp) => transformResponseHandler(resp),
            transformErrorResponse: (error) => transformResponseHandler(error)
        }),

        // Recipes
        getRecipes: builder.query({
            query: () => ({
                url: '/pos/recipes',
                method: 'GET',
            }),
            providesTags: ['Recipes'],
            transformErrorResponse: (error) => transformResponseHandler(error)
        }),
        getRecipeByFoodItem: builder.query({
            query: (food_item_id) => ({
                url: `/pos/recipes/${food_item_id}`,
                method: 'GET',
            }),
            providesTags: (result, error, arg) => [{ type: 'Recipes', id: arg }],
            transformResponse: (resp) => transformResponseHandler(resp, false),
            transformErrorResponse: (error) => transformResponseHandler(error)
        }),
        addUpdateRecipe: builder.mutation({
            query: (body) => ({
                url: '/pos/recipes',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['Recipes'],
            transformResponse: (resp) => transformResponseHandler(resp),
            transformErrorResponse: (error) => transformResponseHandler(error)
        }),
        deleteRecipe: builder.mutation({
            query: (food_item_id) => ({
                url: `/pos/recipes/${food_item_id}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['Recipes'],
            transformResponse: (resp) => transformResponseHandler(resp),
            transformErrorResponse: (error) => transformResponseHandler(error)
        }),

        // Wastage
        getWastages: builder.query({
            query: ({ search, page, perPage } = {}) => {
                const params = {};
                if (search) params.search = search;
                if (page) params.page = page;
                if (perPage) params.perPage = perPage;
                return {
                    url: '/pos/wastages',
                    method: 'GET',
                    params,
                };
            },
            providesTags: ['Wastages'],
            transformErrorResponse: (error) => transformResponseHandler(error)
        }),
        addWastage: builder.mutation({
            query: (body) => ({
                url: '/pos/wastages',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['Wastages', 'InventoryItems'],
            transformResponse: (resp) => transformResponseHandler(resp),
            transformErrorResponse: (error) => transformResponseHandler(error)
        }),
        updateWastage: builder.mutation({
            query: ({ id, ...body }) => ({
                url: `/pos/wastages/${id}`,
                method: 'PUT',
                body,
            }),
            invalidatesTags: ['Wastages', 'InventoryItems'],
            transformResponse: (resp) => transformResponseHandler(resp),
            transformErrorResponse: (error) => transformResponseHandler(error)
        }),
        deleteWastage: builder.mutation({
            query: (id) => ({
                url: `/pos/wastages/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['Wastages', 'InventoryItems'],
            transformResponse: (resp) => transformResponseHandler(resp),
            transformErrorResponse: (error) => transformResponseHandler(error)
        }),

        // Stock Adjustments
        getStockAdjustments: builder.query({
            query: ({ search, page, perPage } = {}) => {
                const params = {};
                if (search) params.search = search;
                if (page) params.page = page;
                if (perPage) params.perPage = perPage;
                return {
                    url: '/pos/stock-adjustments',
                    method: 'GET',
                    params,
                };
            },
            providesTags: ['StockAdjustments'],
            transformErrorResponse: (error) => transformResponseHandler(error)
        }),
        addStockAdjustment: builder.mutation({
            query: (body) => ({
                url: '/pos/stock-adjustments',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['StockAdjustments', 'InventoryItems'],
            transformResponse: (resp) => transformResponseHandler(resp),
            transformErrorResponse: (error) => transformResponseHandler(error)
        }),
        updateStockAdjustment: builder.mutation({
            query: ({ id, ...body }) => ({
                url: `/pos/stock-adjustments/${id}`,
                method: 'PUT',
                body,
            }),
            invalidatesTags: ['StockAdjustments', 'InventoryItems'],
            transformResponse: (resp) => transformResponseHandler(resp),
            transformErrorResponse: (error) => transformResponseHandler(error)
        }),
        deleteStockAdjustment: builder.mutation({
            query: (id) => ({
                url: `/pos/stock-adjustments/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['StockAdjustments', 'InventoryItems'],
            transformResponse: (resp) => transformResponseHandler(resp),
            transformErrorResponse: (error) => transformResponseHandler(error)
        }),
        getInventoryDashboard: builder.query({
            query: (params) => ({
                url: '/pos/dashboard/inventory',
                method: 'GET',
                params,
            }),
            providesTags: ['InventoryDashboard'],
            transformErrorResponse: (error) => transformResponseHandler(error)
        }),
        getAllergies: builder.query({
            query: () => ({
                url: '/get-allergies?perPage=all',
                method: 'GET',
            }),
            providesTags: ['Allergies'],
            transformResponse: (resp) => transformResponseHandler(resp, false),
            transformErrorResponse: (error) => transformResponseHandler(error)
        }),
    })
});

export const {
    useGetInventoryCategoriesQuery,
    useAddInventoryCategoryMutation,
    useUpdateInventoryCategoryMutation,
    useDeleteInventoryCategoryMutation,
    useChangeInventoryCategoryStatusMutation,
    useGetInventoryItemsQuery,
    useAddInventoryItemMutation,
    useUpdateInventoryItemMutation,
    useDeleteInventoryItemMutation,
    useChangeInventoryItemStatusMutation,
    useGetRecipesQuery,
    useGetRecipeByFoodItemQuery,
    useAddUpdateRecipeMutation,
    useDeleteRecipeMutation,
    useGetWastagesQuery,
    useAddWastageMutation,
    useUpdateWastageMutation,
    useDeleteWastageMutation,
    useGetStockAdjustmentsQuery,
    useAddStockAdjustmentMutation,
    useUpdateStockAdjustmentMutation,
    useDeleteStockAdjustmentMutation,
    useGetInventoryDashboardQuery,
    useGetAllergiesQuery,
} = inventoryApi;
